import React, { useState, useMemo } from 'react';
import { Eye, Palette, Thermometer, Sun, Lightbulb, Layers, Blend, Sliders } from 'lucide-react';
import {
    hexToHsl,
    hslToHex,
    hexToRgb,
    simulateColorBlindness,
} from '../../../lib/colorUtils';
import { useToast } from '../../ToastNotifications';

export const VariationsDemo = ({ colors, onColorsChange }) => {
    const [activeVariation, setActiveVariation] = useState('shade');
    const [showHex, setShowHex] = useState(true); // Add state for hex toggle
    const { showCopyToast, showDownloadToast } = useToast();

    // Variation tabs configuration matching the screenshots
    const variationTabs = [
        { id: 'shade', label: 'Shade', icon: Layers },
        { id: 'saturation', label: 'Saturation', icon: Sliders },
        { id: 'hue', label: 'Hue', icon: Palette },
        { id: 'temperature', label: 'Temperature', icon: Thermometer },
        { id: 'blindness', label: 'Blindness', icon: Eye },
        { id: 'luminance', label: 'Luminance', icon: Lightbulb },
        { id: 'gradient', label: 'Gradient', icon: Blend },
        { id: 'theme', label: 'Theme', icon: Sun }
    ];

    // Use more colors from palette (up to 8 for better display, or all if less than 8)
    const baseColors = useMemo(() => {
        if (colors.length <= 8) {
            return colors; // Use all colors if 8 or fewer
        } else {
            return colors.slice(0, 8); // Use first 8 colors for better display
        }
    }, [colors]);

    // Color adjustment functions
    const adjustShade = (hex, percentage) => {
        const [h, s, l] = hexToHsl(hex);
        const adjustment = percentage * 0.7; // Scale for better visual range
        const newL = Math.max(0, Math.min(100, l + adjustment));
        return hslToHex(h, s, newL);
    };

    const adjustSaturation = (hex, percentage) => {
        const [h, s, l] = hexToHsl(hex);
        const adjustment = percentage * 0.7; // Scale for better visual range
        const newS = Math.max(0, Math.min(100, s + adjustment));
        return hslToHex(h, newS, l);
    };

    const adjustHue = (hex, degrees) => {
        const [h, s, l] = hexToHsl(hex);
        const newH = (h + degrees + 360) % 360;
        return hslToHex(newH, s, l);
    };

    const adjustTemperature = (hex, percentage) => {
        const [h, s, l] = hexToHsl(hex);
        let newH = h;

        if (percentage > 0) {
            // Warmer: shift towards red/orange (0-60 degrees)
            const warmTarget = 30; // Orange
            newH = h + (warmTarget - h) * (percentage / 100) * 0.3;
        } else {
            // Cooler: shift towards blue/cyan (180-240 degrees)
            const coolTarget = 210; // Blue
            newH = h + (coolTarget - h) * (Math.abs(percentage) / 100) * 0.3;
        }

        newH = (newH + 360) % 360;
        return hslToHex(newH, s, l);
    };

    const getLuminance = (hex) => {
        const rgb = hexToRgb(hex);
        const [r, g, b] = rgb.map(c => c / 255);
        const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
        return Math.round(luminance * 100);
    };

    // Generate variations based on type
    const generateShadeVariations = () => {
        const percentages = [90, 80, 70, 60, 50, 40, 30, 20, 10, 0, -10, -20, -30, -40, -50, -60, -70, -80, -90];
        return percentages.map(percentage => ({
            percentage: percentage === 0 ? 'Original' : `${percentage > 0 ? '+' : ''}${percentage}%`,
            colors: baseColors.map(color => adjustShade(color.value, percentage)),
            rawPercentage: percentage
        }));
    };

    const generateSaturationVariations = () => {
        const percentages = [90, 80, 70, 60, 50, 40, 30, 20, 10, 0, -10, -20, -30, -40, -50, -60, -70, -80, -90];
        return percentages.map(percentage => ({
            percentage: percentage === 0 ? 'Original' : `${percentage > 0 ? '+' : ''}${percentage}%`,
            colors: baseColors.map(color => adjustSaturation(color.value, percentage)),
            rawPercentage: percentage
        }));
    };

    const generateHueVariations = () => {
        const degrees = [36, 32, 28, 24, 20, 16, 12, 8, 4, 0, -4, -8, -12, -16, -20, -24, -28, -32, -36];
        return degrees.map(degree => ({
            degree: degree === 0 ? 'Original' : `${degree > 0 ? '+' : ''}${degree}°`,
            colors: baseColors.map(color => adjustHue(color.value, degree)),
            rawDegree: degree
        }));
    };

    const generateTemperatureVariations = () => {
        const percentages = [90, 80, 70, 60, 50, 40, 30, 20, 10, 0, -10, -20, -30, -40, -50, -60, -70, -80, -90];
        return percentages.map(percentage => ({
            percentage: percentage === 0 ? 'Original' : `${percentage > 0 ? '+' : ''}${percentage}%`,
            colors: baseColors.map(color => adjustTemperature(color.value, percentage)),
            rawPercentage: percentage
        }));
    };

    const generateBlindnessVariations = () => {
        const blindnessTypes = [
            { key: 'original', label: 'Original' },
            { key: 'protanopia', label: 'Protanopia' },
            { key: 'protanomaly', label: 'Protanomaly' },
            { key: 'deuteranopia', label: 'Deuteranopia' },
            { key: 'deuteranomaly', label: 'Deuteranomaly' },
            { key: 'tritanopia', label: 'Tritanopia' },
            { key: 'tritanomaly', label: 'Tritanomaly' },
            { key: 'achromatopsia', label: 'Achromatopsia' },
            { key: 'achromatomaly', label: 'Achromatomaly' }
        ];

        return blindnessTypes.map(type => ({
            ...type,
            colors: baseColors.map(color =>
                type.key === 'original'
                    ? color.value
                    : simulateColorBlindness(color.value, type.key)
            )
        }));
    };

    const generateLuminanceVariations = () => {
        return baseColors.map(color => {
            const luminance = getLuminance(color.value);
            return {
                color: color.value,
                luminance: luminance
            };
        });
    };

    const generateThemeVariations = () => {
        const lightColors = baseColors.map(color => {
            const [h, s, l] = hexToHsl(color.value);
            const lightL = Math.max(75, Math.min(95, l + 30));
            const lightS = Math.max(20, s - 15);
            return hslToHex(h, lightS, lightL);
        });

        const darkColors = baseColors.map(color => {
            const [h, s, l] = hexToHsl(color.value);
            const darkL = Math.max(5, Math.min(30, l - 30));
            const darkS = Math.min(100, s + 10);
            return hslToHex(h, darkS, darkL);
        });

        return { light: lightColors, dark: darkColors };
    };

    // Copy functionality
    const copyColorRow = (colors) => {
        const hexCodes = colors.join('\n');
        navigator.clipboard.writeText(hexCodes);
        showCopyToast('Color Row', 'hex');
    };

    const exportToVisualizer = (colorsArray) => {
        if (onColorsChange && Array.isArray(colorsArray)) {
            const visualizerColors = colorsArray.map((color, index) => ({
                id: `variation-${index + 1}`,
                value: color
            }));
            onColorsChange(visualizerColors);
        }
    };

    // Render color row
    const renderColorRow = (colors, forceShowHex = false) => (
        <div className="flex h-20 rounded-lg overflow-hidden shadow-sm border border-gray-200 dark:border-gray-600">
            {colors.map((color, index) => (
                <div
                    key={index}
                    className="flex-1 relative group cursor-pointer flex items-center justify-center"
                    style={{ backgroundColor: color }}
                    onClick={() => copyColorRow([color])}
                >
                    {(showHex || forceShowHex) && (
                        <div className="text-center">
                            <div className="text-xs font-mono font-semibold text-white drop-shadow-lg">
                                {color.toUpperCase()}
                            </div>
                        </div>
                    )}
                    <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-10 transition-opacity"></div>
                </div>
            ))}
        </div>
    );

    // Render main content based on active variation
    const renderVariationContent = () => {
        switch (activeVariation) {
            case 'shade':
                const shadeVariations = generateShadeVariations();
                return (
                    <div className="space-y-1">
                        {shadeVariations.map((variation, index) => (
                            <div key={index} className="flex items-center gap-4">
                                <div className="w-20 text-right text-sm font-mono text-gray-600 dark:text-gray-400">
                                    {variation.percentage}
                                </div>
                                <div className="flex-1">
                                    {renderColorRow(variation.colors, variation.rawPercentage === 0 && showHex)}
                                </div>
                            </div>
                        ))}
                    </div>
                );

            case 'saturation':
                const saturationVariations = generateSaturationVariations();
                return (
                    <div className="space-y-1">
                        {saturationVariations.map((variation, index) => (
                            <div key={index} className="flex items-center gap-4">
                                <div className="w-20 text-right text-sm font-mono text-gray-600 dark:text-gray-400">
                                    {variation.percentage}
                                </div>
                                <div className="flex-1">
                                    {renderColorRow(variation.colors, variation.rawPercentage === 0 && showHex)}
                                </div>
                            </div>
                        ))}
                    </div>
                );

            case 'hue':
                const hueVariations = generateHueVariations();
                return (
                    <div className="space-y-1">
                        {hueVariations.map((variation, index) => (
                            <div key={index} className="flex items-center gap-4">
                                <div className="w-20 text-right text-sm font-mono text-gray-600 dark:text-gray-400">
                                    {variation.degree}
                                </div>
                                <div className="flex-1">
                                    {renderColorRow(variation.colors, variation.rawDegree === 0 && showHex)}
                                </div>
                            </div>
                        ))}
                    </div>
                );

            case 'temperature':
                const temperatureVariations = generateTemperatureVariations();
                return (
                    <div className="space-y-1">
                        {temperatureVariations.map((variation, index) => (
                            <div key={index} className="flex items-center gap-4">
                                <div className="w-20 text-right text-sm font-mono text-gray-600 dark:text-gray-400">
                                    {variation.percentage}
                                </div>
                                <div className="flex-1">
                                    {renderColorRow(variation.colors, variation.rawPercentage === 0 && showHex)}
                                </div>
                            </div>
                        ))}
                    </div>
                );

            case 'blindness':
                const blindnessVariations = generateBlindnessVariations();
                return (
                    <div className="space-y-3">
                        {blindnessVariations.map((variation, index) => (
                            <div key={variation.key} className="flex items-center gap-4">
                                <div className="w-32 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                                    {variation.label}
                                </div>
                                <div className="flex-1">
                                    {renderColorRow(variation.colors, variation.key === 'original' && showHex)}
                                </div>
                            </div>
                        ))}
                    </div>
                );

            case 'luminance':
                const luminanceVariations = generateLuminanceVariations();
                return (
                    <div className="flex h-64 rounded-lg overflow-hidden shadow-sm border border-gray-200 dark:border-gray-600">
                        {luminanceVariations.map((item, index) => (
                            <div
                                key={index}
                                className="flex-1 relative flex items-center justify-center"
                                style={{
                                    backgroundColor: `hsl(0, 0%, ${item.luminance}%)`
                                }}
                            >
                                <div className={`text-center font-mono text-lg font-bold ${item.luminance > 50 ? 'text-black' : 'text-white'
                                    }`}>
                                    {item.luminance}%
                                </div>
                            </div>
                        ))}
                    </div>
                );

            case 'gradient':
                const gradientColors = baseColors.map(c => c.value);
                const gradientCSS = `linear-gradient(90deg, ${gradientColors.join(', ')})`;
                return (
                    <div className="space-y-4">
                        <div
                            className="h-64 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600"
                            style={{ background: gradientCSS }}
                        />
                    </div>
                );

            case 'theme':
                const themeVariations = generateThemeVariations();
                return (
                    <div className="space-y-8">
                        {/* Light Theme */}
                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                                    Light
                                </h4>
                            </div>
                            {renderColorRow(themeVariations.light, showHex)}
                        </div>

                        {/* Dark Theme */}
                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                                    Dark
                                </h4>
                            </div>
                            {renderColorRow(themeVariations.dark, showHex)}
                        </div>
                    </div>
                );

            default:
                return <div>Select a variation type</div>;
        }
    };

    if (!baseColors.length) {
        return (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <Palette className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Add some colors to your palette to see variations</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header with Show HEX toggle */}
            <div className="flex justify-between items-center">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Palette Variations
                </h4>
                <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        Show HEX
                        <div className="relative">
                            <input
                                type="checkbox"
                                className="sr-only"
                                checked={showHex}
                                onChange={(e) => setShowHex(e.target.checked)}
                            />
                            <div
                                className={`w-10 h-6 rounded-full shadow-inner flex items-center transition-all duration-200 ${showHex ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
                                    }`}
                            >
                                <div
                                    className={`w-4 h-4 bg-white rounded-full shadow transform transition-transform duration-200 ${showHex ? 'translate-x-4' : 'translate-x-1'
                                        }`}
                                ></div>
                            </div>
                        </div>
                    </label>
                </div>
            </div>

            {/* Variation Tabs */}
            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-2">
                <div className="flex flex-wrap gap-1">
                    {variationTabs.map(tab => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveVariation(tab.id)}
                                className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 ${activeVariation === tab.id
                                    ? 'bg-blue-500 text-white shadow-sm'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                                    }`}
                            >
                                {tab.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Variation Content */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                {renderVariationContent()}
            </div>
        </div>
    );
};

export default VariationsDemo;